import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// "My Day" embed — shows what the user set out to do for the displayed
// day vs what they actually accomplished. Surfaces above the day grid
// on Calendar's Day view so the gap between plan and execution is
// visible at the top of the page.
//
// Reads from GET /api/me/daily-accomplishments. The parent passes the
// displayed Date plus the blocks array (already fetched for the calendar
// grid) so we can render an immediate skeleton from the blocks while the
// API round-trip fills in the closed-tasks/items detail.
import { useEffect, useMemo, useState } from "react";
import { api } from "../api/client";
function ymd(d) {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
// Monday-of-this-week as YYYY-MM-DD in local time. Used as the week key
// when adding a task back to the kill list from the "Not yet" action.
function mondayOfThisWeekISO() {
    const now = new Date();
    const dow = (now.getDay() + 6) % 7;
    const monday = new Date(now);
    monday.setDate(now.getDate() - dow);
    const y = monday.getFullYear();
    const m = String(monday.getMonth() + 1).padStart(2, "0");
    const d = String(monday.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}
export default function MyDaySummary({ onDate, blocks, onBlockUpdated, onOpenProgressPrompt, refreshSignal, }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Per-block busy state for the inline Done / Not yet actions — keeps
    // either button from being clicked twice while a request is in flight.
    // Keyed by block id so multiple rows can resolve in parallel.
    const [reviewBusy, setReviewBusy] = useState({});
    const [reviewOpen, setReviewOpen] = useState(false);
    // Per-habit busy state for the daily day-streak check-in Yes/No buttons.
    const [habitBusy, setHabitBusy] = useState({});
    const [mirrorProposals, setMirrorProposals] = useState([]);
    const [proposalBusy, setProposalBusy] = useState({});
    // "Needs follow-up" — lead follow-up reminders + tracked-email reply nudges
    // due today or overdue (GET /crm/needs-follow-up). Shown only on today's
    // view; it's a reminder box, never a calendar block.
    const [followUps, setFollowUps] = useState([]);
    const [followUpBusy, setFollowUpBusy] = useState({});
    // Default expanded for today, collapsed for past/future days.
    const isToday = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const target = new Date(onDate);
        target.setHours(0, 0, 0, 0);
        return target.getTime() === today.getTime();
    }, [onDate]);
    const [expanded, setExpanded] = useState(isToday);
    // Helper: refetch open mirror proposals from the notification feed.
    // Filters client-side because the existing /notifications endpoint
    // doesn't take a type query param; the list is small (max 50). We
    // skip read notifications so accepted/declined rows drop out.
    // Follow-ups due now or soon (lead reminders + email reply nudges). Only
    // meaningful on today's view, so callers skip it for past/future days.
    async function loadFollowUps() {
        try {
            setFollowUps(await api.get("/crm/needs-follow-up"));
        }
        catch {
            /* swallow — non-fatal, the box just stays empty */
        }
    }
    async function loadMirrorProposals() {
        try {
            const rows = await api.get("/notifications?unread_only=true");
            const filtered = rows
                .filter((r) => r.type === "mirror_proposal" && r.payload)
                .map((r) => ({
                id: r.id,
                message: r.message,
                payload: (r.payload ?? {}),
            }));
            setMirrorProposals(filtered);
        }
        catch {
            /* swallow — non-fatal, the section just stays empty */
        }
    }
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        (async () => {
            try {
                const d = await api.get(`/me/daily-accomplishments?on_date=${ymd(onDate)}`);
                if (!cancelled)
                    setData(d);
                if (!cancelled)
                    await loadMirrorProposals();
                if (!cancelled && isToday)
                    await loadFollowUps();
            }
            catch (e) {
                if (!cancelled)
                    setError(e instanceof Error ? e.message : "Couldn't load day");
            }
            finally {
                if (!cancelled)
                    setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [onDate]);
    // Silent refetch whenever the parent bumps refreshSignal (calendar
    // reloaded after a progress-modal save). Skips the initial mount —
    // refreshSignal starts undefined and the [onDate] effect already did the
    // first load. Without this the awaiting-review count stayed stuck on the
    // stale value after the user hit "Not yet" until they changed the day.
    useEffect(() => {
        if (refreshSignal === undefined)
            return;
        void reloadDay();
        void loadMirrorProposals();
        if (isToday)
            void loadFollowUps();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshSignal]);
    // Completion %: planned-vs-completed by hours. When nothing is planned,
    // we show "—" instead of a divide-by-zero NaN.
    const completionPct = useMemo(() => {
        if (!data || data.planned_hours <= 0)
            return null;
        return Math.round((data.completed_hours / data.planned_hours) * 100);
    }, [data]);
    // Re-fetch the daily endpoint so the awaiting list shrinks immediately
    // after the user resolves an item (instead of waiting for the next poll
    // or parent refresh).
    async function reloadDay() {
        try {
            const d = await api.get(`/me/daily-accomplishments?on_date=${ymd(onDate)}`);
            setData(d);
        }
        catch (_) {
            // Best-effort — the next normal load picks up the new state. We
            // already removed the row optimistically via setReviewBusy below.
        }
    }
    // "Complete" — flip the block's is_completed flag and let the backend
    // cascade tick the linked checklist item + recompute task progress.
    // Same code path as the Complete? button on the calendar tile, just
    // surfaced inline. Bubbles up via onBlockUpdated so the parent
    // Calendar refetches its block list — keeps the calendar tile and the
    // banner in sync (previously only this banner refreshed and the tile
    // still showed Complete?).
    async function markReviewDone(blockId) {
        setReviewBusy((b) => ({ ...b, [blockId]: true }));
        setError(null);
        try {
            await api.patch(`/time-blocks/${blockId}`, { is_completed: true });
            await reloadDay();
            onBlockUpdated?.();
        }
        catch (e) {
            // Without this catch the rejected promise was unhandled and the
            // row just sat there — the classic "clicked Done, nothing
            // happened" silent failure. Surface it on the existing error
            // line so the user knows to retry.
            setError(e instanceof Error
                ? `Couldn't mark complete: ${e.message}`
                : "Couldn't mark complete");
        }
        finally {
            setReviewBusy((b) => {
                const next = { ...b };
                delete next[blockId];
                return next;
            });
        }
    }
    // "Not yet" — open the full ProgressPromptModal so the user can enter
    // % progress, then choose Reschedule / Handoff / Schedule later. The
    // modal handles the Kill List re-add internally when the user picks
    // Schedule later. If the parent didn't wire up onOpenProgressPrompt,
    // fall back to the legacy inline kill-list add so the button still
    // does something useful.
    async function acceptMirrorProposal(p) {
        setProposalBusy((b) => ({ ...b, [p.id]: true }));
        try {
            await api.post(`/notifications/${p.id}/mirror-proposal/accept`, {});
            // Remove from local state + tell the parent to refetch its
            // blocks so the calendar tile for the conflicting block
            // disappears and the new meeting block appears.
            setMirrorProposals((cur) => cur.filter((x) => x.id !== p.id));
            onBlockUpdated?.();
        }
        catch (e) {
            setError(e instanceof Error
                ? `Couldn't accept: ${e.message}`
                : "Couldn't accept the meeting");
        }
        finally {
            setProposalBusy((b) => {
                const next = { ...b };
                delete next[p.id];
                return next;
            });
        }
    }
    async function declineMirrorProposal(p) {
        setProposalBusy((b) => ({ ...b, [p.id]: true }));
        try {
            await api.post(`/notifications/${p.id}/mirror-proposal/decline`, {});
            setMirrorProposals((cur) => cur.filter((x) => x.id !== p.id));
        }
        catch (e) {
            setError(e instanceof Error
                ? `Couldn't decline: ${e.message}`
                : "Couldn't decline");
        }
        finally {
            setProposalBusy((b) => {
                const next = { ...b };
                delete next[p.id];
                return next;
            });
        }
    }
    async function markReviewNotYet(row) {
        if (onOpenProgressPrompt) {
            onOpenProgressPrompt(row);
            return;
        }
        setReviewBusy((b) => ({ ...b, [row.id]: true }));
        setError(null);
        try {
            if (row.task_id) {
                try {
                    await api.post("/kill-list", {
                        task_id: row.task_id,
                        week_start: mondayOfThisWeekISO(),
                    });
                }
                catch (_e) {
                    // 409 = already on the list, swallow. Other failures silently
                    // skipped — user can re-try from the calendar tile.
                }
            }
            await reloadDay();
            onBlockUpdated?.();
        }
        catch (e) {
            // reloadDay() could still reject (e.g. the daily endpoint 500s) —
            // surface it instead of leaving an unhandled rejection.
            setError(e instanceof Error
                ? `Couldn't update: ${e.message}`
                : "Couldn't update");
        }
        finally {
            setReviewBusy((b) => {
                const next = { ...b };
                delete next[row.id];
                return next;
            });
        }
    }
    // Answer a day-streak goal's once-per-day review. "Yes" extends the streak
    // (or restarts it after a missed day); "No" resets it to 0. Reloads the day
    // so the prompt clears immediately.
    async function answerHabit(goalId, reviewDate, completed) {
        setHabitBusy((b) => ({ ...b, [goalId]: true }));
        setError(null);
        try {
            await api.post(`/me/goals/${goalId}/habit-checkin`, {
                date: reviewDate,
                completed,
            });
            await reloadDay();
        }
        catch (e) {
            setError(e instanceof Error
                ? `Couldn't save check-in: ${e.message}`
                : "Couldn't save check-in");
        }
        finally {
            setHabitBusy((b) => {
                const next = { ...b };
                delete next[goalId];
                return next;
            });
        }
    }
    // ── "Needs follow-up" box actions ──────────────────────────────────
    // A lead's follow-up reminder: "Done" clears the date off the lead.
    async function clearLeadFollowUp(item) {
        if (!item.lead_id)
            return;
        setFollowUpBusy((b) => ({ ...b, [item.id]: true }));
        try {
            await api.del(`/crm/leads/${item.lead_id}/follow-up`);
            setFollowUps((cur) => cur.filter((x) => x.id !== item.id));
        }
        catch (e) {
            setError(e instanceof Error ? e.message : "Couldn't clear follow-up");
        }
        finally {
            setFollowUpBusy((b) => {
                const next = { ...b };
                delete next[item.id];
                return next;
            });
        }
    }
    // A tracked email: "Replied" clears it; "Snooze" pushes the reply window.
    async function markEmailReplied(item) {
        setFollowUpBusy((b) => ({ ...b, [item.id]: true }));
        try {
            await api.post(`/reply-tracker/${item.id}/replied`, {});
            setFollowUps((cur) => cur.filter((x) => x.id !== item.id));
        }
        catch (e) {
            setError(e instanceof Error ? e.message : "Couldn't update");
        }
        finally {
            setFollowUpBusy((b) => {
                const next = { ...b };
                delete next[item.id];
                return next;
            });
        }
    }
    async function snoozeEmail(item) {
        setFollowUpBusy((b) => ({ ...b, [item.id]: true }));
        try {
            await api.post(`/reply-tracker/${item.id}/snooze`, { days: 2 });
            setFollowUps((cur) => cur.filter((x) => x.id !== item.id));
        }
        catch (e) {
            setError(e instanceof Error ? e.message : "Couldn't snooze");
        }
        finally {
            setFollowUpBusy((b) => {
                const next = { ...b };
                delete next[item.id];
                return next;
            });
        }
    }
    // Open the lead in the CRM (Resources → Company Tools → Lead Pipeline). Set
    // the tab intent so Resources lands on the CRM tab, then navigate.
    function openLeadInCrm() {
        try {
            localStorage.setItem("resources-tools-tab", "crm");
        }
        catch {
            /* ignore */
        }
    }
    // Pre-API fallback from the blocks prop — gives instant first paint.
    const fallback = useMemo(() => {
        const now = new Date();
        let planned_blocks = 0;
        let planned_min = 0;
        let completed_blocks = 0;
        let completed_min = 0;
        let awaiting = 0;
        for (const b of blocks) {
            const start = new Date(b.start_at);
            const end = new Date(b.end_at);
            const sameDay = start.getFullYear() === onDate.getFullYear() &&
                start.getMonth() === onDate.getMonth() &&
                start.getDate() === onDate.getDate();
            if (!sameDay)
                continue;
            const dur = (end.getTime() - start.getTime()) / 60000;
            planned_blocks += 1;
            planned_min += dur;
            if (b.is_completed) {
                completed_blocks += 1;
                completed_min += dur;
            }
            else if (end < now) {
                // Mirror the backend's exclusion: personal blocks don't count
                // toward the awaiting-review nudge.
                const isPersonal = b.is_personal || b.block_type === "personal";
                if (!isPersonal)
                    awaiting += 1;
            }
        }
        return {
            planned_blocks,
            planned_hours: Math.round((planned_min / 60) * 10) / 10,
            completed_blocks,
            completed_hours: Math.round((completed_min / 60) * 10) / 10,
            awaiting,
        };
    }, [blocks, onDate]);
    if (error)
        return _jsx("div", { className: "my-day-embed error", children: error });
    // Use API data when available, else the live fallback.
    const planned_blocks = data?.planned_blocks ?? fallback.planned_blocks;
    const planned_hours = data?.planned_hours ?? fallback.planned_hours;
    const completed_blocks = data?.completed_blocks ?? fallback.completed_blocks;
    const completed_hours = data?.completed_hours ?? fallback.completed_hours;
    const awaiting = data?.awaiting_review_blocks ?? fallback.awaiting;
    const closes = (data?.tasks_completed_today ?? 0) + (data?.items_completed_today ?? 0);
    return (_jsxs("section", { className: `my-day-embed ${expanded ? "expanded" : "collapsed"}`, children: [_jsxs("button", { type: "button", className: "my-day-embed-head", onClick: () => setExpanded((v) => !v), "aria-expanded": expanded, children: [_jsxs("div", { children: [_jsxs("span", { className: "my-day-embed-eyebrow", children: [isToday ? "Today" : "That day", " \u00B7 planned vs accomplished"] }), _jsxs("span", { className: "my-day-embed-summary", children: [planned_blocks, " block", planned_blocks === 1 ? "" : "s", " \u00B7", " ", planned_hours, "h scheduled \u00B7 ", completed_blocks, "/", planned_blocks, " ", "completed", closes > 0 && ` · ${closes} closed`, awaiting > 0 && (_jsxs(_Fragment, { children: [" ", "\u00B7", " ", _jsxs("span", { className: "my-day-nudge", children: [awaiting, " past block", awaiting === 1 ? "" : "s", " awaiting review"] })] }))] })] }), _jsx("span", { className: "widget-toggle-icon", "aria-hidden": "true", children: expanded ? "−" : "+" })] }), expanded && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "my-day-bar-wrap", children: [_jsx("div", { className: "my-day-bar-track", children: _jsx("div", { className: "my-day-bar-fill", style: {
                                        width: planned_hours > 0
                                            ? `${Math.min(100, Math.round((completed_hours / planned_hours) * 100))}%`
                                            : "0%",
                                    } }) }), _jsx("div", { className: "my-day-bar-label", children: completionPct === null
                                    ? "Nothing scheduled today yet."
                                    : `${completionPct}% of today's plan done — ${completed_hours}h of ${planned_hours}h` })] }), _jsxs("div", { className: "my-day-tiles", children: [_jsxs("div", { className: "my-day-tile", children: [_jsx("div", { className: "tile-label", children: "Deep Focus" }), _jsxs("div", { className: "tile-value", children: [data?.completed_deep_focus_blocks ?? 0, _jsxs("span", { className: "tile-value-sub", children: [" ", "/ ", data?.planned_deep_focus_blocks ?? 0] })] }), _jsxs("div", { className: "tile-delta", children: [Math.round((data?.completed_deep_focus_hours ?? 0) * 10) / 10, "h of", " ", Math.round((data?.planned_deep_focus_hours ?? 0) * 10) / 10, "h done"] })] }), _jsxs("div", { className: "my-day-tile", children: [_jsx("div", { className: "tile-label", children: "Tasks closed today" }), _jsx("div", { className: "tile-value", children: data?.tasks_completed_today ?? 0 }), _jsxs("div", { className: "tile-delta", children: [data?.items_completed_today ?? 0, " checklist items done"] })] }), _jsxs("button", { type: "button", className: `my-day-tile my-day-tile-button ${reviewOpen ? "expanded" : ""}`, onClick: () => awaiting > 0 ? setReviewOpen((v) => !v) : undefined, disabled: awaiting === 0, title: awaiting === 0
                                    ? "No past blocks waiting review"
                                    : "Click to mark each waiting block Done or Not yet", children: [_jsx("div", { className: "tile-label", children: "Awaiting review" }), _jsxs("div", { className: "tile-value", children: [awaiting, awaiting > 0 && (_jsx("span", { className: "my-day-tile-caret", "aria-hidden": "true", children: reviewOpen ? "▾" : "▸" }))] }), _jsx("div", { className: "tile-delta", children: awaiting === 0
                                            ? "you're caught up"
                                            : reviewOpen
                                                ? "tap Complete or Not yet"
                                                : "click to review" })] })] }), isToday && followUps.length > 0 && (_jsxs("div", { className: "my-day-awaiting my-day-followups", children: [_jsxs("div", { className: "my-day-section-head", children: [_jsx("strong", { children: "\uD83D\uDCCC Needs follow-up" }), _jsx("span", { className: "muted small", children: "Reminders due today or overdue \u2014 nothing's on your calendar." })] }), _jsx("ul", { className: "my-day-awaiting-list", children: followUps.map((f) => {
                                    const busy = !!followUpBusy[f.id];
                                    return (_jsxs("li", { children: [_jsxs("div", { className: "my-day-awaiting-info", children: [_jsxs("div", { className: "my-day-awaiting-title", children: [_jsx("span", { className: "my-day-fu-kind", "aria-hidden": "true", children: f.kind === "email_reply" ? "📨" : "🎯" }), " ", f.title] }), _jsxs("div", { className: `muted small ${f.overdue ? "my-day-fu-overdue" : ""}`, children: [f.subtitle, f.overdue ? " · overdue" : ""] })] }), _jsx("div", { className: "my-day-awaiting-actions", children: f.kind === "email_reply" ? (_jsxs(_Fragment, { children: [_jsx("button", { type: "button", className: "btn-primary", disabled: busy, onClick: () => markEmailReplied(f), title: "They replied \u2014 clear it", children: busy ? "…" : "✓ Replied" }), _jsx("button", { type: "button", disabled: busy, onClick: () => snoozeEmail(f), title: "Wait 2 more days", children: "Snooze 2d" })] })) : (_jsxs(_Fragment, { children: [_jsx("a", { className: "my-day-fu-open", href: "/resources", onClick: openLeadInCrm, title: "Open the lead in the CRM", children: "Open lead \u2197" }), _jsx("button", { type: "button", className: "btn-primary", disabled: busy, onClick: () => clearLeadFollowUp(f), title: "Clear this follow-up off the lead", children: busy ? "…" : "✓ Done" })] })) })] }, `${f.kind}-${f.id}`));
                                }) })] })), mirrorProposals.length > 0 && (_jsxs("div", { className: "my-day-awaiting", children: [_jsxs("div", { className: "my-day-section-head", children: [_jsx("strong", { children: "Meeting requests waiting on you" }), _jsx("span", { className: "muted small", children: "A teammate tried to book a shared meeting at a slot you already have something at." })] }), _jsx("ul", { className: "my-day-awaiting-list", children: mirrorProposals.map((p) => {
                                    const busy = !!proposalBusy[p.id];
                                    const start = p.payload.proposed_start_at
                                        ? new Date(p.payload.proposed_start_at)
                                        : null;
                                    const end = p.payload.proposed_end_at
                                        ? new Date(p.payload.proposed_end_at)
                                        : null;
                                    const when = start && end
                                        ? `${start.toLocaleString([], {
                                            weekday: "short",
                                            month: "short",
                                            day: "numeric",
                                            hour: "numeric",
                                            minute: "2-digit",
                                        })} – ${end.toLocaleTimeString([], {
                                            hour: "numeric",
                                            minute: "2-digit",
                                        })}`
                                        : "";
                                    return (_jsxs("li", { children: [_jsxs("div", { className: "my-day-awaiting-info", children: [_jsxs("div", { className: "my-day-awaiting-title", children: [_jsxs("span", { className: "muted small", children: ["From ", p.payload.proposer_name ?? "a teammate", " ", "\u00B7", " "] }), p.payload.task_title ?? "Shared meeting"] }), _jsx("div", { className: "muted small", children: when })] }), _jsxs("div", { className: "my-day-awaiting-actions", children: [_jsx("button", { type: "button", className: "btn-primary", disabled: busy, onClick: () => acceptMirrorProposal(p), title: "Replace your existing block with this meeting", children: busy ? "…" : "Accept" }), _jsx("button", { type: "button", disabled: busy, onClick: () => declineMirrorProposal(p), title: "Keep your existing block \u2014 dismisses the request", children: "Decline" })] })] }, p.id));
                                }) })] })), data && data.habit_checkins.length > 0 && (_jsxs("div", { className: "my-day-awaiting", children: [_jsxs("div", { className: "my-day-section-head", children: [_jsx("strong", { children: "Daily habits \u2014 keep your streak honest" }), _jsx("span", { className: "muted small", children: "Did you keep these up yesterday?" })] }), _jsx("ul", { className: "my-day-awaiting-list", children: data.habit_checkins.map((h) => {
                                    const busy = !!habitBusy[h.goal_id];
                                    return (_jsxs("li", { children: [_jsxs("div", { className: "my-day-awaiting-info", children: [_jsx("div", { className: "my-day-awaiting-title", children: h.title }), _jsxs("div", { className: "muted small", children: ["Current streak: ", h.streak, " day", h.streak === 1 ? "" : "s", h.target ? ` · goal ${h.target}` : ""] })] }), _jsxs("div", { className: "my-day-awaiting-actions", children: [_jsx("button", { type: "button", className: "btn-primary", disabled: busy, onClick: () => answerHabit(h.goal_id, h.review_date, true), title: "Yes \u2014 extend the streak", children: busy ? "…" : "Yes" }), _jsx("button", { type: "button", disabled: busy, onClick: () => answerHabit(h.goal_id, h.review_date, false), title: "No \u2014 resets the streak to zero", children: "No" })] })] }, h.goal_id));
                                }) })] })), reviewOpen && data && data.awaiting_review_items.length > 0 && (_jsxs("div", { className: "my-day-awaiting", children: [_jsxs("div", { className: "my-day-section-head", children: [_jsx("strong", { children: "Past blocks waiting on you" }), _jsx("span", { className: "muted small", children: "Not yet \u2192 puts the task back on your Kill List for this week." })] }), _jsx("ul", { className: "my-day-awaiting-list", children: data.awaiting_review_items.map((b) => {
                                    const busy = !!reviewBusy[b.id];
                                    return (_jsxs("li", { children: [_jsxs("div", { className: "my-day-awaiting-info", children: [_jsxs("div", { className: "my-day-awaiting-title", children: [b.project_name && (_jsxs("span", { className: "muted small", children: [b.project_name, b.task_title && ` · ${b.task_title}`, " · "] })), b.title] }), _jsxs("div", { className: "muted small", children: ["Ended", " ", new Date(b.end_at).toLocaleTimeString([], {
                                                                hour: "numeric",
                                                                minute: "2-digit",
                                                            })] })] }), _jsxs("div", { className: "my-day-awaiting-actions", children: [_jsx("button", { type: "button", className: "btn-primary", disabled: busy, onClick: () => markReviewDone(b.id), title: "Mark the calendar block + linked task complete", children: busy ? "…" : "Complete" }), _jsx("button", { type: "button", disabled: busy, onClick: () => markReviewNotYet(b), title: b.task_id
                                                            ? "Leave incomplete + re-add to this week's Kill List"
                                                            : "Dismiss — no linked task to re-add", children: "Not yet" })] })] }, b.id));
                                }) })] })), loading && !data && (_jsx("div", { className: "muted small", style: { marginTop: "0.3rem" }, children: "Refreshing today's numbers\u2026" })), data &&
                        (data.completed_tasks.length > 0 ||
                            data.completed_items.length > 0) && (_jsxs("div", { className: "my-day-closed", children: [_jsx("div", { className: "my-day-section-head", children: _jsx("strong", { children: "Closed today" }) }), _jsxs("ul", { className: "my-day-closed-list", children: [data.completed_tasks.slice(0, 5).map((t) => {
                                        // Build the "Project Name: Task Title (2 of 3)" tail
                                        // so users see project context + which slot in the
                                        // project flow this was without expanding anything.
                                        const tail = t.flow_position != null && t.flow_total != null
                                            ? ` (${t.flow_position} of ${t.flow_total})`
                                            : "";
                                        const label = t.project_name
                                            ? `${t.project_name}: ${t.title}${tail}`
                                            : `${t.title}${tail}`;
                                        return (_jsxs("li", { children: [_jsx("span", { className: "my-day-check done", children: "\u2713" }), t.project_id ? (_jsx("a", { href: `/projects?project=${encodeURIComponent(t.project_id)}`, target: "_blank", rel: "noopener noreferrer", className: "my-day-closed-title my-day-closed-project", title: "Open this project in a new tab", children: label })) : (_jsx("span", { className: "my-day-closed-title", children: label }))] }, `t-${t.id}`));
                                    }), data.completed_items.slice(0, 3).map((c) => (_jsxs("li", { children: [_jsx("span", { className: "my-day-check done", children: "\u2713" }), _jsx("span", { className: "my-day-closed-title", children: c.title }), c.project_id ? (_jsxs("a", { href: `/projects?project=${encodeURIComponent(c.project_id)}`, target: "_blank", rel: "noopener noreferrer", className: "muted small my-day-closed-project", title: "Open this project in a new tab", children: [c.project_name ?? "No project", " \u00B7 ", c.task_title] })) : (_jsxs("span", { className: "muted small", children: ["No project \u00B7 ", c.task_title] }))] }, `i-${c.id}`)))] })] }))] }))] }));
}
